import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Search, Sparkles, LogOut, Bookmark, User2, CreditCard, GalleryHorizontal } from 'lucide-react';
import { Logo } from '@/components/common/logo';
import { Container } from './container';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuthModal } from '@/stores/auth-modal';
import { useAuth, useAuthBootstrap } from '@/stores/auth';
import { signOutAndRedirect } from '@/lib/auth-client';
import { toast } from 'sonner';
import { cn } from '@/lib/cn';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const navItems: Array<{ to: string; label: string; activeSearch?: string; icon?: typeof GalleryHorizontal }> = [
  { to: '/explore', label: 'Explore' },
  { to: '/categories', label: 'Categories' },
  { to: '/showcase', label: 'Showcase', icon: GalleryHorizontal },
];

export const TopNav = () => {
  useAuthBootstrap();
  const openAuth = useAuthModal((s) => s.openAuth);
  const { isAuthenticated, user, refresh } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const onSignOut = () => {
    void signOutAndRedirect(() => navigate('/'), async (ok) => {
      await refresh();
      toast.success(ok ? 'Signed out' : 'Session ended locally');
    });
  };

  return (
    <header className="sticky top-0 z-40 border-b border-border-subtle bg-background/88 backdrop-blur-xl supports-[backdrop-filter]:bg-background/72">
      <Container size="xl" className="flex h-16 items-center gap-2 sm:gap-4">
        <Logo size="md" />

        <nav className="hidden md:flex items-center gap-1 ml-4">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  cn(
                    'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-full text-text-secondary hover:text-foreground hover:bg-surface-soft/70 transition-all',
                    (isActive || (item.activeSearch && location.search.includes(item.activeSearch))) &&
                      'bg-accent-soft text-foreground ring-1 ring-accent/20',
                  )
                }
              >
                {Icon && <Icon className="h-3.5 w-3.5" aria-hidden="true" />}
                {item.label}
              </NavLink>
            );
          })}
          {isAuthenticated && (
            <NavLink
              to="/saved"
              className={({ isActive }) =>
                cn(
                  'px-3 py-1.5 text-sm rounded-full text-text-secondary hover:text-foreground hover:bg-surface-soft/70 transition-all',
                  isActive && 'bg-accent-soft text-foreground ring-1 ring-accent/20',
                )
              }
            >
              Saved
            </NavLink>
          )}
          {isAuthenticated && (
            <NavLink
              to="/collections"
              className={({ isActive }) =>
                cn(
                  'px-3 py-1.5 text-sm rounded-full text-text-secondary hover:text-foreground hover:bg-surface-soft/70 transition-all',
                  isActive && 'bg-accent-soft text-foreground ring-1 ring-accent/20',
                )
              }
            >
              Collections
            </NavLink>
          )}
          {isAuthenticated && (
            <NavLink
              to="/generate"
              className={({ isActive }) =>
                cn(
                  'px-3 py-1.5 text-sm rounded-full text-text-secondary hover:text-foreground hover:bg-surface-soft/70 transition-all',
                  isActive && 'bg-accent-soft text-foreground ring-1 ring-accent/20',
                )
              }
            >
              Create
            </NavLink>
          )}
        </nav>

        <form
          className="ml-auto hidden lg:flex items-center relative w-72"
          onSubmit={(e) => {
            e.preventDefault();
            const q = new FormData(e.currentTarget).get('q');
            if (typeof q === 'string' && q.trim()) {
              navigate(`/search?q=${encodeURIComponent(q.trim())}`);
            }
          }}
        >
          <Search className="absolute left-3 h-4 w-4 text-text-muted pointer-events-none" aria-hidden="true" />
          <Input
            name="q"
            placeholder="Search prompts, styles, brands, categories..."
            className="pl-9 h-9 text-sm bg-surface/70 ring-1 ring-border-subtle focus-visible:ring-accent/50"
            defaultValue={location.pathname === '/search' ? new URLSearchParams(location.search).get('q') ?? '' : ''}
          />
        </form>

        <div className="ml-auto lg:ml-0 flex min-w-0 items-center gap-1.5 sm:gap-2">
          {isAuthenticated ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2">
                  <span className="grid h-6 w-6 place-items-center rounded-full bg-accent-soft text-accent text-xs font-semibold ring-1 ring-accent/25">
                    {(user?.name || user?.email || 'U').charAt(0).toUpperCase()}
                  </span>
                  <span className="hidden sm:inline">{user?.name || user?.email?.split('@')[0]}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span className="text-foreground">{user?.name || 'Visualry member'}</span>
                    <span className="text-xs text-text-muted font-normal">{user?.email}</span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => navigate('/saved')}>
                  <Bookmark className="h-3.5 w-3.5" aria-hidden="true" /> Saved prompts
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => navigate('/collections')}>
                  <User2 className="h-3.5 w-3.5" aria-hidden="true" /> Collections
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => navigate('/account')}>
                  <CreditCard className="h-3.5 w-3.5" aria-hidden="true" /> Account
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={onSignOut}>
                  <LogOut className="h-3.5 w-3.5" aria-hidden="true" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button
                variant="ghost"
                size="sm"
                className="hidden sm:inline-flex"
                onClick={() => openAuth('signin')}
              >
                Sign in
              </Button>
              <Button
                size="sm"
                className="hidden gap-1.5 px-2.5 sm:inline-flex sm:px-3"
                onClick={() => openAuth('signup')}
              >
                <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
                Create free account
              </Button>
            </>
          )}
        </div>
      </Container>
    </header>
  );
};

export const MobileSearchBar = () => {
  const navigate = useNavigate();
  return (
    <div className="lg:hidden border-b border-border-subtle bg-background/86 backdrop-blur-xl sticky top-16 z-30">
      <Container size="xl" className="py-2.5">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const q = new FormData(e.currentTarget).get('q');
            if (typeof q === 'string' && q.trim()) {
              navigate(`/search?q=${encodeURIComponent(q.trim())}`);
            }
          }}
          className="relative"
        >
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-muted pointer-events-none" aria-hidden="true" />
          <Input
            name="q"
            placeholder="Search prompts, styles, brands, categories..."
            className="pl-9 h-10 text-sm bg-surface/70 ring-1 ring-border-subtle"
          />
        </form>
      </Container>
    </div>
  );
};
